import type { Patch } from "@freightsimple/shared/types";
import type { ErrorMessageType } from "@freightsimple/ui-core/FormLayout/FormItem";
import type { InputWidth } from "@freightsimple/ui-core/Controls/inputWidth";
import { usePostalCodesApi } from "@/Hooks/usePostalCodesApi";
import type { Address } from "@freightsimple/generated-dashboard-openapi-client/models/Address";
import type { LatitudeLongitude } from "@freightsimple/generated-dashboard-openapi-client/models/LatitudeLongitude";
import { CountryCode } from "@freightsimple/generated-dashboard-openapi-client/models/CountryCode";
import { ResponseError } from "@freightsimple/generated-dashboard-openapi-client/runtime";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { geocodeCity } from "@/Shared/Maps/geocodeCity";
import { isValidPostalCodeFormat } from "../Validators/locationsErrorMessageForPostalCode";
import LocationsPostalCodeField from "./LocationsPostalCodeField";

export interface PostalCodeChangeEvent {
  address: Patch<Address>;
  latitudeLongitude?: LatitudeLongitude | undefined;
}

type PostalCodeInputProps = {
  postalCode: string;
  city: string;
  stateOrProvinceCode: string;
  countryCode: CountryCode | undefined;
  onChange: (
    _: PostalCodeChangeEvent,
    distributionWarehouseName: string | undefined,
  ) => void;

  label?: ReactNode;
  width?: InputWidth | undefined;

  errorMessage: ErrorMessageType;
  warningMessage: ErrorMessageType;
  forceValidation: boolean;

  displayName: string | undefined;

  enabled?: boolean | undefined;

  cities?: string[] | undefined;

  /** Show the location preview under the field once coordinates resolve. */
  showMap: boolean;
};

const LocationsPostalCodeInput: React.FC<PostalCodeInputProps> = (
  props: PostalCodeInputProps,
) => {
  const [loading, setLoading] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [cities, setCities] = useState<string[] | undefined>(props.cities);
  const [lookupUnavailable, setLookupUnavailable] = useState(false);

  // These values come from the postal code lookup API response and need to
  // be preserved across city selections. When the user picks a different city
  // from the dropdown, we must forward them to the parent via props.onChange
  // — without storing them, they'd be lost since the API response is gone.
  const [distributionWarehouseName, setDistributionWarehouseName] = useState<
    string | undefined
  >(undefined);
  const [latitudeLongitude, setLatitudeLongitude] = useState<
    LatitudeLongitude | undefined
  >(undefined);

  const postalCodesApi = usePostalCodesApi();
  const postalCodesApiRef = useRef(postalCodesApi);
  postalCodesApiRef.current = postalCodesApi;

  // When the component mounts with a pre-filled postal code but no cities data
  // (e.g., modifying an existing location), fetch cities from the API so the
  // multi-city dropdown appears immediately.
  //
  // Flip the ref on first render regardless of whether we fetch — otherwise
  // update() filling in a valid postal code later would re-trigger this effect
  // and race its own lookup.
  const hasFetchedInitialCities = useRef(false);
  useEffect(() => {
    if (hasFetchedInitialCities.current) return;
    hasFetchedInitialCities.current = true;

    if (props.cities !== undefined) return;
    if (!isValidPostalCodeFormat(props.postalCode)) return;

    async function fetchCities() {
      try {
        const response = await postalCodesApiRef.current.lookUpCity({
          postalCode: props.postalCode.replace(/ /g, ""),
        });
        setCities(response.cities);
        setDistributionWarehouseName(response.distributionWarehouseName);
        if (
          response.longitude !== undefined &&
          response.latitude !== undefined
        ) {
          setLatitudeLongitude({
            longitude: response.longitude,
            latitude: response.latitude,
          });
        }
      } catch (e) {
        console.error("Error fetching cities for existing postal code", { e });
      }
    }

    fetchCities();
  }, [props.cities, props.postalCode]);

  function emitChange(
    address: Patch<Address>,
    coordinates: LatitudeLongitude | undefined,
    warehouseName: string | undefined,
  ) {
    props.onChange({ address, latitudeLongitude: coordinates }, warehouseName);
  }

  // Geocodes a city to get precise coordinates, then updates
  // both local state and the parent via emitChange.
  async function geocodeAndUpdateCoordinates(
    city: string,
    stateOrProvinceCode: string,
    postalCode: string,
    address: Patch<Address>,
    warehouseName: string | undefined,
  ) {
    const result = await geocodeCity(city, stateOrProvinceCode, postalCode);
    if (result) {
      const coordinates = {
        longitude: result.longitude,
        latitude: result.latitude,
      };
      setLatitudeLongitude(coordinates);
      emitChange(address, coordinates, warehouseName);
    }
    // Silent failure is intentional: geocoding is a best-effort enhancement
    // for minimap precision. The city name is already updated via emitChange
    // before this function is called, so the user's selection is preserved
    // even if geocoding fails. The map will show the previous coordinates.
  }

  async function handleCitySelected(city: string) {
    const address: Patch<Address> = {
      postalCode: props.postalCode,
      countryCode: props.countryCode,
      stateOrProvinceCode: props.stateOrProvinceCode,
      city: city,
    };

    // Immediately update the city so the UI is responsive
    emitChange(address, latitudeLongitude, distributionWarehouseName);

    // Show loading skeleton on the minimap while geocoding.
    // Use a minimum delay so the skeleton is visible as user feedback,
    // even when geocoding resolves quickly.
    setMapLoading(true);

    const minimumDelay = new Promise((resolve) => setTimeout(resolve, 300));

    await Promise.all([
      geocodeAndUpdateCoordinates(
        city,
        props.stateOrProvinceCode,
        props.postalCode,
        address,
        distributionWarehouseName,
      ),
      minimumDelay,
    ]);

    setMapLoading(false);
  }

  async function update(postalCode: string) {
    setLookupUnavailable(false);
    setCities(undefined);
    setDistributionWarehouseName(undefined);
    setLatitudeLongitude(undefined);

    emitChange(
      {
        postalCode: postalCode,
        countryCode: undefined,
        stateOrProvinceCode: undefined,
        city: undefined,
      },
      undefined,
      undefined,
    );

    if (!isValidPostalCodeFormat(postalCode)) {
      return;
    }

    setLoading(true);

    try {
      const response = await postalCodesApi.lookUpCity({
        postalCode: postalCode.replace(/ /g, ""),
      });

      const coordinates =
        response.longitude !== undefined && response.latitude !== undefined
          ? { longitude: response.longitude, latitude: response.latitude }
          : undefined;

      const address: Patch<Address> = {
        postalCode: postalCode,
        countryCode: response.countryCode,
        stateOrProvinceCode: response.stateOrProvinceCode,
        city: response.city,
      };

      setLoading(false);
      setCities(response.cities);
      setDistributionWarehouseName(response.distributionWarehouseName);
      setLatitudeLongitude(coordinates);
      emitChange(address, coordinates, response.distributionWarehouseName);

      // The API returns zip code center coordinates which may not match
      // the primary city. Geocode to get precise coordinates for the minimap.
      const hasMultipleCities =
        response.cities !== undefined && response.cities.length > 1;

      if (hasMultipleCities && response.city && response.stateOrProvinceCode) {
        await geocodeAndUpdateCoordinates(
          response.city,
          response.stateOrProvinceCode,
          postalCode,
          address,
          response.distributionWarehouseName,
        );
      }
    } catch (e) {
      setLoading(false);
      if (e instanceof ResponseError && e.response.status === 503) {
        setLookupUnavailable(true);
        return;
      }
      console.error("Error looking up postal code", { e });
      alert("Error with postal code");
    }
  }

  return (
    <LocationsPostalCodeField
      postalCode={props.postalCode}
      city={props.city}
      cities={cities}
      onCitySelected={handleCitySelected}
      countryCode={props.countryCode}
      stateOrProvinceCode={props.stateOrProvinceCode}
      onUpdate={update}
      loading={loading}
      label={props.label}
      errorMessage={props.errorMessage}
      warningMessage={props.warningMessage}
      forceValidation={props.forceValidation}
      width={props.width}
      displayName={props.displayName}
      enabled={props.enabled === undefined ? true : props.enabled}
      showMap={props.showMap}
      latitudeLongitude={latitudeLongitude}
      mapLoading={mapLoading}
      lookupUnavailable={lookupUnavailable}
      onRetryLookup={() => update(props.postalCode)}
    ></LocationsPostalCodeField>
  );
};

export default LocationsPostalCodeInput;
